import React, { useCallback, useState } from 'react';
import {
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiHelpCircle,
  FiLock,
  FiMap,
  FiRefreshCw,
  FiSliders,
} from 'react-icons/fi';
import { useI18n } from '../../i18n';
import './PayrollWorkspace.css';

interface PayrollWorkflowProps {
  canManageRates: boolean;
  currentSubIndex: number;
  onNavigate: (subIndex: number) => void;
  onReplayTour: () => void;
}

const STORAGE_KEY = 'odinseye.payroll.workflowOpen';

const workflowSteps = [
  {
    number: 1,
    title: 'Configure rates',
    body: 'Set effective-dated pay bands and company payroll rules.',
    subIndex: 2,
    target: 'payroll-bands-list',
    adminOnly: true,
    icon: FiSliders,
  },
  {
    number: 2,
    title: 'Calculate timesheets',
    body: 'Create a pay period and calculate it from clocked shift records.',
    subIndex: 1,
    target: 'payroll-timesheet-periods',
    icon: FiRefreshCw,
  },
  {
    number: 3,
    title: 'Review and approve',
    body: 'Resolve exceptions, add adjustments and approve every employee.',
    subIndex: 1,
    target: 'payroll-timesheet-review',
    icon: FiCheckCircle,
  },
  {
    number: 4,
    title: 'Lock and export',
    body: 'An admin locks and exports the period, then marks it paid after provider confirmation.',
    subIndex: 1,
    target: 'payroll-period-actions',
    icon: FiLock,
  },
] as const;

/** Read once on mount; a throwing localStorage (private mode) just means closed. */
function readStoredOpen(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export default function PayrollWorkflow({
  canManageRates,
  currentSubIndex,
  onNavigate,
  onReplayTour,
}: PayrollWorkflowProps) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(readStoredOpen);

  const toggle = useCallback(() => {
    setIsOpen((open) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, String(!open));
      } catch {
        // Preference only; the guide still opens for this session.
      }
      return !open;
    });
  }, []);

  const openStep = (subIndex: number, target: string) => {
    onNavigate(subIndex);
    window.setTimeout(() => {
      document.querySelector<HTMLElement>(`[data-tour="${target}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  };

  return (
    <section
      className={`pay-workflow ${isOpen ? 'pay-workflow--open' : ''}`}
      data-tour="payroll-workflow"
      aria-labelledby="payroll-workflow-title"
    >
      <button
        type="button"
        className="pay-workflow__summary"
        onClick={toggle}
        aria-expanded={isOpen}
        aria-controls="payroll-workflow-body"
      >
        <FiMap aria-hidden="true" />
        <span className="pay-workflow__eyebrow" id="payroll-workflow-title">{t('Payroll workflow')}</span>
        <span className="pay-workflow__rail">
          {workflowSteps.map((step, position) => (
            <React.Fragment key={step.number}>
              {position > 0 && <FiChevronRight aria-hidden="true" size={12} />}
              <span
                className={`pay-workflow__rail-step ${step.subIndex === currentSubIndex ? 'pay-workflow__rail-step--here' : ''}`}
              >
                {step.number}. {t(step.title)}
              </span>
            </React.Fragment>
          ))}
        </span>
        <FiChevronDown className="pay-workflow__chevron" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="pay-workflow__body" id="payroll-workflow-body">
          <ol className="pay-workflow__steps">
            {workflowSteps.map((step) => {
              const Icon = step.icon;
              const canOpen = !('adminOnly' in step && step.adminOnly) || canManageRates;
              return (
                <li key={step.number} className="pay-workflow__step">
                  <button
                    type="button"
                    className="pay-workflow__step-button"
                    onClick={() => openStep(step.subIndex, step.target)}
                    disabled={!canOpen}
                    title={!canOpen ? t('Pay rates are managed by company admins.') : undefined}
                  >
                    <span className="pay-workflow__icon"><Icon aria-hidden="true" /></span>
                    <span className="pay-workflow__copy">
                      <span className="pay-workflow__number">{t('Step {number}', { number: step.number })}</span>
                      <strong>{t(step.title)}</strong>
                      <span>{t(step.body)}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="pay-workflow__footer">
            <button type="button" className="btn btn--secondary" onClick={onReplayTour}>
              <FiHelpCircle aria-hidden="true" />
              {t('How payroll works')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
